import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Restaurant } from "./Restaurant";

export const AdminPage = () => {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token");
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [data, setData] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  
  // Local state for editing
  const [announcementEnabled, setAnnouncementEnabled] = useState(false);
  const [announcement, setAnnouncement] = useState({ title: "", message: "" });
  const [menuSections, setMenuSections] = useState<any[]>([]);

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      try {
        const secretSnap = await getDoc(doc(db, "secrets", "admin"));
        const secretToken = secretSnap.data()?.token;

        if (tokenFromUrl && tokenFromUrl === secretToken) {
          setIsAuthorized(true);
          const docSnap = await getDoc(doc(db, "config", "restaurant"));
          if (docSnap.exists()) {
            const resData = docSnap.data() as Restaurant;
            setData(resData);
            setAnnouncementEnabled(!!resData.announcement);
            setAnnouncement(resData.announcement || { title: "", message: "" });
            setMenuSections(resData.menu.sections || []);
          }
        } else {
          setIsAuthorized(false);
        }
      } catch (err) {
        console.error("Auth/Fetch error:", err);
        setIsAuthorized(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuthAndFetch();
  }, [tokenFromUrl]);

  const handleSave = async () => {
    if (!tokenFromUrl || !data) return;
    setSaving(true);
    setMessage("");
    try {
      const docRef = doc(db, "config", "restaurant");
      const updatedData: any = {
        ...data,
        menu: { ...data.menu, sections: menuSections },
        adminToken: tokenFromUrl 
      };

      if (announcementEnabled) {
        updatedData.announcement = announcement;
      } else {
        updatedData.announcement = null; 
      }

      await updateDoc(docRef, updatedData);
      setMessage("Sparat framgångsrikt!");
      window.scrollTo(0, 0);
    } catch (err) {
      console.error("Save error:", err);
      setMessage("Misslyckades att spara.");
    } finally {
      setSaving(false);
    }
  };

  const updateSectionHeader = (index: number, val: string) => {
    const newSections = [...menuSections];
    newSections[index].header = val;
    setMenuSections(newSections);
  };

  const updateItem = (sIndex: number, iIndex: number, field: string, val: string) => {
    const newSections = [...menuSections];
    newSections[sIndex].items[iIndex][field] = val;
    setMenuSections(newSections);
  };

  const removeItem = (sIndex: number, iIndex: number) => {
    if (!window.confirm("Är du säker på att du vill ta bort rätten?")) return;
    const newSections = [...menuSections];
    newSections[sIndex].items.splice(iIndex, 1);
    setMenuSections(newSections);
  };

  const addItem = (sIndex: number) => {
    const newSections = [...menuSections];
    newSections[sIndex].items.push({ title: "Ny rätt", description: "", price: "" });
    setMenuSections(newSections);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  if (loading) return <div className="p-10 text-center text-xl font-bold">Laddar admin...</div>;
  if (isAuthorized === false) return <div className="p-10 text-center text-red-500 font-bold text-3xl">404 - Sidan hittades inte</div>;

  return (
    <div className="bg-gray-100 min-h-screen">
      {/* Top Header */}
      <div className="bg-white border-b sticky top-0 z-30 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Sushi Koi Admin</h1>
          <div className="flex gap-4">
            <button
              className={`${saving ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"} text-white font-bold py-2 px-6 rounded-lg transition-all text-sm sm:text-base`}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Sparar..." : "Spara Allt"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col md:flex-row gap-8">
        
        {/* Navigation Sidebar */}
        <div className="md:w-64 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-sm border p-4 sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto">
            <h3 className="font-bold text-gray-500 uppercase text-xs mb-4 tracking-wider px-2">Snabbnavigering</h3>
            <nav className="space-y-1">
              <button 
                onClick={() => scrollToSection("announcement-section")}
                className="w-full text-left px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 text-gray-700"
              >
                📢 Meddelande
              </button>
              <div className="my-2 border-t border-gray-100"></div>
              {menuSections.map((section, idx) => (
                <button 
                  key={idx}
                  onClick={() => scrollToSection(`section-${idx}`)}
                  className="w-full text-left px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 text-gray-700 truncate"
                  title={section.header}
                >
                  🍽️ {section.header}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-grow max-w-3xl">
          {message && (
            <div className={`mb-6 p-4 rounded-lg text-center font-bold shadow-sm ${message.includes("Misslyckades") ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
              {message}
            </div>
          )}

          {/* Announcement Section */}
          <div id="announcement-section" className="bg-white shadow-sm rounded-lg p-6 mb-8 border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <span className="text-2xl">📢</span> Meddelande på hemsidan
              </h2>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={announcementEnabled} 
                  onChange={() => setAnnouncementEnabled(!announcementEnabled)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                <span className="ml-3 text-sm font-medium text-gray-700">{announcementEnabled ? "Visas" : "Dold"}</span>
              </label>
            </div>
            
            {announcementEnabled && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-600 mb-1">Rubrik</label>
                  <input
                    className="w-full border rounded-lg p-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all"
                    value={announcement.title}
                    onChange={(e) => setAnnouncement({ ...announcement, title: e.target.value })}
                    placeholder="T.ex. Välkommen till oss!"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-600 mb-1">Text</label>
                  <textarea
                    className="w-full border rounded-lg p-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none h-32 transition-all"
                    value={announcement.message}
                    onChange={(e) => setAnnouncement({ ...announcement, message: e.target.value })}
                    placeholder="T.ex. På grund av högtider har vi öppet..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Menu Sections */}
          <div className="flex items-center gap-4 mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Matsedel</h2>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>
          
          {menuSections.map((section, sIndex) => (
            <div key={sIndex} id={`section-${sIndex}`} className="bg-white shadow-sm rounded-lg p-6 mb-8 border border-gray-200 scroll-mt-24">
              <div className="flex items-center gap-2 mb-6">
                 <span className="text-xl">🍽️</span>
                 <input
                  className="text-xl font-bold w-full border-b-2 border-transparent focus:border-red-500 outline-none text-red-700 transition-all py-1 px-1"
                  value={section.header}
                  onChange={(e) => updateSectionHeader(sIndex, e.target.value)}
                />
              </div>
              
              <div className="space-y-4">
                {section.items.map((item: any, iIndex: number) => (
                  <div key={iIndex} className="p-4 bg-gray-50 rounded-xl border border-gray-200 relative group hover:bg-white hover:shadow-md transition-all">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-grow space-y-2">
                        <input
                          className="w-full font-bold bg-transparent border-b border-transparent focus:border-blue-400 outline-none"
                          value={item.title}
                          placeholder="Namn på rätten"
                          onChange={(e) => updateItem(sIndex, iIndex, "title", e.target.value)}
                        />
                        <input
                          className="w-full text-sm text-gray-500 bg-transparent border-b border-transparent focus:border-blue-400 outline-none"
                          value={item.description}
                          placeholder="Beskrivning (ingredienser)"
                          onChange={(e) => updateItem(sIndex, iIndex, "description", e.target.value)}
                        />
                      </div>
                      <div className="w-full sm:w-28 flex items-center justify-end gap-2">
                        <input
                          className="w-20 font-bold text-red-600 text-right bg-transparent border-b border-transparent focus:border-blue-400 outline-none"
                          value={item.price}
                          placeholder="Pris"
                          onChange={(e) => updateItem(sIndex, iIndex, "price", e.target.value)}
                        />
                        <button 
                          onClick={() => removeItem(sIndex, iIndex)}
                          className="text-gray-300 hover:text-red-500 font-bold px-2 transition-colors"
                          title="Ta bort rätt"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                <button 
                  onClick={() => addItem(sIndex)}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:bg-white hover:border-gray-400 hover:text-gray-700 font-bold transition-all mt-4"
                >
                  + Lägg till ny rätt i {section.header}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Floating Save Button for Mobile */}
      <div className="md:hidden fixed bottom-6 right-6 z-40">
        <button
          className={`${saving ? "bg-gray-400" : "bg-green-600 shadow-2xl scale-110 active:scale-95"} text-white font-bold h-16 w-16 rounded-full transition-all flex items-center justify-center`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "..." : "✓"}
        </button>
      </div>
    </div>
  );
};
